import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Zap,
  ChevronDown,
  ChevronRight,
  Check,
  CircleCheck,
  Circle,
  X,
  RefreshCw,
  FileSearch,
  Upload,
  LoaderCircle,
  Plus,
  TriangleAlert,
  Filter,
  Lock,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { usePermissions } from '@/lib/usePermissions';
import { useSubmissionFocus } from '@/lib/SubmissionFocusContext';
import { useDevMode } from '@/lib/DevModeContext';
import { SectionPicker } from '@/modules/settings/SectionPicker';
import { AcceptUnexpectedDialog } from './AcceptUnexpectedDialog';
import { CTD_SECTIONS } from '@/lib/ctdSections';
import { Telemetry } from '@/lib/telemetry';
import type { ClassificationTypeState, ClassificationSetMethod } from '@/lib/telemetry-types';
import { startTimer, endTimer } from '@/lib/telemetry-timers';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { MODULE_COLORS } from '@/design/tokens';
import type {
  Submission,
  SubmissionTypeOption,
  SubmissionClassification,
  SectionSuggestion,
} from '@/types/api';

// Track in-flight FDA letter analyses so the spinner survives navigation
const inflightAnalyses = new Map<string, Promise<SubmissionClassification>>();

const CATEGORY_LABELS: Record<string, string> = {
  initial: 'Initial',
  clinical: 'Clinical',
  cmc: 'CMC',
  safety: 'Safety',
  nonclinical: 'Nonclinical',
  regulatory: 'Regulatory',
  maintenance: 'Maintenance',
  administrative: 'Administrative',
  'expanded-access': 'Expanded Access',
  other: 'Other',
};

// Display order for the "Change type" dropdown groups. The backend
// SUBMISSION_TYPES dict puts `other` last, but we surface it at the top
// (just above Initial) so users see the catch-all without scrolling
// through nine groups. Unknown categories fall to the end.
const CATEGORY_ORDER: string[] = [
  'other',
  'initial',
  'clinical',
  'cmc',
  'safety',
  'nonclinical',
  'regulatory',
  'maintenance',
  'administrative',
  'expanded-access',
];

function moduleLabel(mod: string) {
  const key = `M${mod}`;
  return MODULE_COLORS[key]?.label ?? mod;
}

export function SubmissionClassificationPage() {
  const { activeProject } = useProject();
  const { canRunScans, canDismissFindings } = usePermissions();
  const { devMode } = useDevMode();
  const projectId = activeProject?.id ?? '';
  const { submissionFocusId } = useSubmissionFocus();

  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [allTypes, setAllTypes] = useState<SubmissionTypeOption[]>([]);
  const [classifications, setClassifications] = useState<Map<string, SubmissionClassification>>(new Map());
  const [expandedSubId, setExpandedSubId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Per-expanded-submission state (only one expanded at a time)
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [inferring, setInferring] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissError, setDismissError] = useState('');
  const [showAddSection, setShowAddSection] = useState(false);
  const [addingSectionError, setAddingSectionError] = useState('');

  // A2F-565: bulk-accept-unexpected dialog state.
  const [acceptUnexpectedOpen, setAcceptUnexpectedOpen] = useState(false);
  const [acceptingUnexpected, setAcceptingUnexpected] = useState(false);
  const [acceptUnexpectedError, setAcceptUnexpectedError] = useState('');
  const [driftedSections, setDriftedSections] = useState<string[] | null>(null);

  // Filter
  const [filterNeedsAttention, setFilterNeedsAttention] = useState(false);

  // Ref for scrolling to triage panel on type click
  const triagePanelRef = useRef<HTMLDivElement>(null);

  // Load submissions + types + classifications on mount
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const [subs, types] = await Promise.all([
          api.projects.submissions(projectId, true),
          api.submissionTypes.listAll(),
        ]);
        if (cancelled) return;
        setSubmissions(subs);
        setAllTypes(types);

        // Pre-fetch classifications for all submissions in parallel
        const classResults = await Promise.allSettled(
          subs.map(async (s) => {
            try {
              const cls = await api.submissionTypes.getClassification(projectId, s.id);
              return [s.id, cls] as const;
            } catch {
              return null;
            }
          }),
        );
        if (cancelled) return;
        const classMap = new Map<string, SubmissionClassification>();
        for (const r of classResults) {
          if (r.status === 'fulfilled' && r.value) {
            classMap.set(r.value[0], r.value[1]);
          }
        }
        setClassifications(classMap);

        // Auto-expand from global focus if set
        if (submissionFocusId && subs.some(s => s.id === submissionFocusId)) {
          setExpandedSubId(submissionFocusId);
        }
      } catch {
        // silently ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [projectId]);

  // Sync selection when global focus changes (e.g., sidebar picker)
  useEffect(() => {
    if (!submissionFocusId || submissions.length === 0) return;
    if (submissions.some(s => s.id === submissionFocusId)) {
      setExpandedSubId(submissionFocusId);
    }
  }, [submissionFocusId, submissions]);

  // Helper: update a single classification in the map
  const updateClassification = useCallback((subId: string, cls: SubmissionClassification) => {
    setClassifications(prev => {
      const next = new Map(prev);
      next.set(subId, cls);
      return next;
    });
  }, []);

  // Load/reload classification for a single submission
  const loadClassification = useCallback(async (subId: string) => {
    setInferring(true);
    try {
      const data = await api.submissionTypes.getClassification(projectId, subId);
      updateClassification(subId, data);
    } catch {
      // keep existing
    } finally {
      setInferring(false);
    }
  }, [projectId, updateClassification]);

  // A2F-529: fire classification_viewed once per expand so we can compute
  // override/confirm/silent-acceptance rates. Per-session dedupe is computable
  // downstream in PostHog if ever needed.
  const fireClassificationViewed = useCallback((subId: string) => {
    const cls = classifications.get(subId);
    const typeState = (cls?.typeCode ? cls.typeState : 'unclassified') as ClassificationTypeState;
    Telemetry.classificationViewed({
      type_state: typeState,
      type_code: cls?.typeCode ?? null,
    });
  }, [classifications]);

  // Expand / collapse
  const handleExpand = useCallback((subId: string) => {
    const isExpanding = expandedSubId !== subId;
    if (isExpanding) fireClassificationViewed(subId);
    setExpandedSubId(prev => {
      if (prev === subId) return null; // collapse
      // Reset per-panel state
      setTypeDropdownOpen(false);
      setDismissingId(null);
      setDismissReason('');
      setDismissError('');
      setShowAddSection(false);
      setAddingSectionError('');
      setAnalyzeError('');
      return subId;
    });
  }, [expandedSubId, fireClassificationViewed]);

  // Click on type label in collapsed row → expand and scroll to triage
  const handleTypeClick = useCallback((subId: string) => {
    const isExpanding = expandedSubId !== subId;
    if (isExpanding) fireClassificationViewed(subId);
    setExpandedSubId(prev => {
      if (prev === subId) return subId; // already expanded, keep open
      setTypeDropdownOpen(false);
      setDismissingId(null);
      setDismissReason('');
      setDismissError('');
      setShowAddSection(false);
      setAddingSectionError('');
      setAnalyzeError('');
      return subId;
    });
    // Scroll to triage panel after render
    requestAnimationFrame(() => {
      triagePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, [expandedSubId, fireClassificationViewed]);

  // Resume spinner if an analysis is still in-flight
  useEffect(() => {
    if (!expandedSubId) return;
    const inflight = inflightAnalyses.get(expandedSubId);
    if (inflight) {
      setAnalyzing(true);
      inflight
        .then((data) => updateClassification(expandedSubId, data))
        .catch(() => {})
        .finally(() => setAnalyzing(false));
    }
  }, [expandedSubId, updateClassification]);

  // --- Action handlers (scoped to expanded submission) ---

  const expandedSub = expandedSubId ? submissions.find(s => s.id === expandedSubId) ?? null : null;
  const classification = expandedSubId ? classifications.get(expandedSubId) ?? null : null;

  const handleSetType = async (typeCode: string) => {
    if (!expandedSubId) return;
    setSaving(true);
    setTypeDropdownOpen(false);
    // Capture prior state BEFORE the API call — used for A2F-529 override telemetry.
    const fromTypeCode = classification?.typeCode ?? null;
    const fromTypeState: ClassificationTypeState = fromTypeCode
      ? (classification?.typeState as ClassificationTypeState)
      : 'unclassified';
    const method: ClassificationSetMethod = !fromTypeCode
      ? 'initial_set'
      : fromTypeState === 'inferred'
        ? 'override_inferred'
        : fromTypeState === 'declared'
          ? 'override_declared'
          : 'override_validated';
    try {
      const data = await api.submissionTypes.setClassification(projectId, expandedSubId, typeCode);
      updateClassification(expandedSubId, data);
      Telemetry.classificationTypeSet({
        type_code: typeCode,
        from_type_state: fromTypeState,
        from_type_code: fromTypeCode,
        method,
      });
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  const handleConfirm = async () => {
    if (!expandedSubId || !classification?.typeCode) return;
    const typeCode = classification.typeCode;
    setSaving(true);
    try {
      await api.submissionTypes.confirm(projectId, expandedSubId);
      await loadClassification(expandedSubId);
      Telemetry.classificationConfirmed({ type_code: typeCode });
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!expandedSubId) return;
    const fromTypeCode = classification?.typeCode ?? null;
    const fromTypeState: ClassificationTypeState = fromTypeCode
      ? (classification?.typeState as ClassificationTypeState)
      : 'unclassified';
    setSaving(true);
    try {
      await api.submissionTypes.reset(projectId, expandedSubId);
      await loadClassification(expandedSubId);
      Telemetry.classificationReset({
        from_type_state: fromTypeState,
        from_type_code: fromTypeCode,
      });
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  const handleRefreshPresence = async () => {
    if (!expandedSubId) return;
    setRefreshing(true);
    try {
      const data = await api.submissionTypes.checkPresence(projectId, expandedSubId);
      updateClassification(expandedSubId, data);
    } catch { /* ignore */ } finally { setRefreshing(false); }
  };

  const handleAnalyzeLetter = async (file: File) => {
    if (!expandedSubId) return;
    setAnalyzing(true);
    setAnalyzeError('');
    startTimer('resolve_analysis');
    Telemetry.resolveAnalysisStarted({ text_length: file.size });

    const subId = expandedSubId;
    const promise = api.submissionTypes.analyzeLetter(projectId, subId, file);
    inflightAnalyses.set(subId, promise);

    try {
      const data = await promise;
      updateClassification(subId, data);
      Telemetry.resolveAnalysisCompleted({
        question_count: data.sections?.length ?? 0,
        section_count: data.sections?.filter((s) => s.isRecommended && !s.dismissed).length ?? 0,
        duration_ms: endTimer('resolve_analysis'),
      });
    } catch (err) {
      endTimer('resolve_analysis');
      const msg = err instanceof Error ? err.message : 'Analysis failed';
      setAnalyzeError(msg);
      Telemetry.errorSwallowed({ error_type: 'analysis_failure', operation: 'resolve_analyze_letter', screen_name: '/plan/sections', error_message: msg });
    } finally {
      inflightAnalyses.delete(subId);
      setAnalyzing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClearAnalysis = async () => {
    if (!expandedSubId) return;
    setSaving(true);
    try {
      const data = await api.submissionTypes.clearAnalysis(projectId, expandedSubId);
      updateClassification(expandedSubId, data);
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  const handleDismiss = async (suggestionId: string) => {
    if (!expandedSubId || dismissReason.trim().length < 10) {
      setDismissError('Reason must be at least 10 characters');
      return;
    }
    setDismissError('');
    try {
      await api.submissionTypes.dismiss(projectId, expandedSubId, suggestionId, dismissReason.trim());
      Telemetry.classificationSuggestionDismissed({ section_number: suggestionId });
      setDismissingId(null);
      setDismissReason('');
      await loadClassification(expandedSubId);
    } catch { setDismissError('Failed to dismiss'); }
  };

  const handleUndismiss = async (suggestionId: string) => {
    if (!expandedSubId) return;
    try {
      await api.submissionTypes.undismiss(projectId, expandedSubId, suggestionId);
      await loadClassification(expandedSubId);
    } catch { /* ignore */ }
  };

  const handleAddSection = async (sectionNumber: string) => {
    if (!expandedSubId) return;
    setAddingSectionError('');
    const title = CTD_SECTIONS[sectionNumber] || '';
    const module = sectionNumber.split('.')[0];
    try {
      const data = await api.submissionTypes.addSection(projectId, expandedSubId, sectionNumber, title, module);
      updateClassification(expandedSubId, data);
      setShowAddSection(false);
    } catch (err) {
      setAddingSectionError(err instanceof Error ? err.message : 'Failed to add section');
    }
  };

  // A2F-565: bulk-accept every currently-observed (basis='observed') section
  // into this submission's manual section list. Stale-state 409 surfaces a
  // precise banner in the dialog — no auto-retry, user refreshes manually.
  const handleOpenAcceptUnexpected = () => {
    setDriftedSections(null);
    setAcceptUnexpectedError('');
    setAcceptUnexpectedOpen(true);
  };

  const handleCloseAcceptUnexpected = () => {
    setAcceptUnexpectedOpen(false);
    setAcceptingUnexpected(false);
    setDriftedSections(null);
    setAcceptUnexpectedError('');
  };

  const handleConfirmAcceptUnexpected = async (sectionsToAccept: string[]) => {
    if (!expandedSubId) return;
    setAcceptingUnexpected(true);
    setAcceptUnexpectedError('');
    setDriftedSections(null);
    try {
      const data = await api.submissionTypes.acceptUnexpected(projectId, expandedSubId, sectionsToAccept);
      updateClassification(expandedSubId, data);
      Telemetry.classificationUnexpectedSectionsBulkAccepted({
        project_id: projectId,
        submission_id: expandedSubId,
        section_count: sectionsToAccept.length,
        section_numbers: sectionsToAccept,
      });
      setAcceptUnexpectedOpen(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to accept sections';
      // apiFetch throws `Error("API error 409: {...json...}")` — extract the
      // drifted_sections list from the embedded body when status is 409.
      const m409 = msg.match(/API error 409:\s*(\{.*\})$/s);
      if (m409) {
        try {
          const body = JSON.parse(m409[1]);
          const drifted = body?.detail?.drifted_sections;
          if (Array.isArray(drifted) && drifted.length > 0) {
            setDriftedSections(drifted);
            // Refetch so the next retry reflects server truth.
            await loadClassification(expandedSubId);
            return;
          }
        } catch {
          // fall through to generic error
        }
      }
      setAcceptUnexpectedError(msg);
    } finally {
      setAcceptingUnexpected(false);
    }
  };

  // --- Rendering ---

  if (loading) return <LoadingSpinner message="Loading submissions..." />;

  if (submissions.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <div className="text-center">
          <FileSearch className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">No submissions found</p>
          <p className="text-xs mt-1">Add an IND with submissions to get started.</p>
        </div>
      </div>
    );
  }

  // Group types by category for dropdown
  const typesByCategory: Record<string, SubmissionTypeOption[]> = {};
  for (const t of allTypes) {
    (typesByCategory[t.category] ??= []).push(t);
  }

  // Only show planning submissions (A2F-492) — matches Planning View behavior.
  const planningSubmissions = submissions.filter(s => s.isPlanning);

  if (planningSubmissions.length === 0) {
    return (
      <div className="flex items-center justify-center py-20 bg-white rounded-xl border border-slate-200">
        <div className="text-center">
          <p className="text-sm font-medium text-slate-500">No submissions selected for planning</p>
          <p className="text-xs text-slate-400 mt-1">Choose which submissions to track in the Section Map.</p>
        </div>
      </div>
    );
  }

  const sortedSubmissions = [...planningSubmissions].reverse();

  // Apply filter
  const filteredSubmissions = filterNeedsAttention
    ? sortedSubmissions.filter(sub => {
        const cls = classifications.get(sub.id);
        if (!cls?.typeCode) return true; // no type = needs attention
        if (cls.typeState === 'inferred') return true; // unconfirmed
        if (cls.missingCount > 0) return true; // has gaps
        return false;
      })
    : sortedSubmissions;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-brand-charcoal">Section Map</h1>
          <p className="text-sm text-slate-500 mt-1">
            Classify submission types and review recommended CTD sections across all submissions.
          </p>
        </div>
        <button
          onClick={() => setFilterNeedsAttention(!filterNeedsAttention)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
            filterNeedsAttention
              ? 'border-amber-300 bg-amber-50 text-amber-700'
              : 'border-slate-200 text-slate-500 hover:bg-slate-50'
          }`}
        >
          <Filter className="w-3.5 h-3.5" />
          Needs Attention
          {filterNeedsAttention && (
            <span className="ml-1 bg-amber-200 text-amber-800 rounded-full px-1.5 text-[10px] font-bold">
              {filteredSubmissions.length}
            </span>
          )}
        </button>
      </div>

      {/* Accordion list */}
      <div className="space-y-2">
        {filteredSubmissions.map(sub => {
          const cls = classifications.get(sub.id);
          const isExpanded = expandedSubId === sub.id;
          const needsAttention = !cls?.typeCode || cls.typeState === 'inferred' || (cls.missingCount ?? 0) > 0;

          return (
            <div key={sub.id} className={`bg-white rounded-xl border ${isExpanded ? 'border-brand-orange/40' : 'border-slate-200'} overflow-hidden`}>
              {/* Collapsed row */}
              <button
                onClick={() => handleExpand(sub.id)}
                className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-slate-50/50 transition-colors"
              >
                <ChevronRight className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />

                {/* Submission number */}
                <span className="text-sm font-bold text-brand-charcoal whitespace-nowrap flex items-center gap-1.5">
                  Sub {sub.displaySequence}
                  {sub.completedAt && (
                    <span title={`Completed on ${new Date(sub.completedAt).toLocaleDateString()}`}>
                      <CircleCheck className="w-3.5 h-3.5 text-emerald-500" />
                    </span>
                  )}
                </span>

                {/* Type label — clickable to expand + scroll to triage */}
                {cls?.typeLabel ? (
                  <span
                    onClick={(e) => { e.stopPropagation(); handleTypeClick(sub.id); }}
                    className="text-xs font-medium text-brand-orange hover:underline cursor-pointer truncate"
                  >
                    {cls.typeLabel}
                    <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      cls.typeState === 'validated' ? 'bg-green-100 text-green-700'
                        : cls.typeState === 'declared' ? 'bg-blue-100 text-blue-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {cls.typeState}
                    </span>
                  </span>
                ) : (
                  <span
                    onClick={(e) => { e.stopPropagation(); handleTypeClick(sub.id); }}
                    className="text-xs text-slate-400 hover:text-brand-orange hover:underline cursor-pointer"
                  >
                    Not classified
                  </span>
                )}

                {/* Spacer */}
                <span className="flex-1" />

                {/* Section coverage */}
                {cls?.typeCode && cls.totalSuggested > 0 ? (
                  <span className="text-xs text-slate-500 whitespace-nowrap">
                    <span className="font-medium text-brand-charcoal">{cls.totalSuggested}</span> sections
                    <span className="mx-1">&middot;</span>
                    <span className="text-green-600">{cls.presentCount} present</span>
                    {cls.missingCount > 0 && (
                      <><span className="mx-1">&middot;</span><span className="text-amber-600">{cls.missingCount} missing</span></>
                    )}
                  </span>
                ) : null}

                {/* Doc count */}
                <span className="text-xs text-slate-400 whitespace-nowrap">{sub.documentCount} docs</span>

                {/* Amber warning indicator with tooltip */}
                {needsAttention && !isExpanded && (
                  <span
                    title={
                      !cls?.typeCode
                        ? 'No submission type assigned'
                        : cls.typeState === 'inferred'
                          ? 'Submission type is AI-inferred — click to confirm'
                          : `${cls.missingCount} missing section${cls.missingCount === 1 ? '' : 's'}`
                    }
                  >
                    <TriangleAlert className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  </span>
                )}
              </button>

              {/* Expanded panel */}
              {isExpanded && (
                <ExpandedPanel
                  sub={sub}
                  classification={classification}
                  typesByCategory={typesByCategory}
                  allTypes={allTypes}
                  inferring={inferring}
                  analyzing={analyzing}
                  analyzeError={analyzeError}
                  saving={saving}
                  refreshing={refreshing}
                  typeDropdownOpen={typeDropdownOpen}
                  dismissingId={dismissingId}
                  dismissReason={dismissReason}
                  dismissError={dismissError}
                  showAddSection={showAddSection}
                  addingSectionError={addingSectionError}
                  canRunScans={canRunScans}
                  canDismissFindings={canDismissFindings}
                  devMode={devMode}
                  fileInputRef={fileInputRef}
                  triagePanelRef={triagePanelRef}
                  onSetType={handleSetType}
                  onConfirm={handleConfirm}
                  onReset={handleReset}
                  onRefreshPresence={handleRefreshPresence}
                  onAnalyzeLetter={handleAnalyzeLetter}
                  onClearAnalysis={handleClearAnalysis}
                  onDismiss={handleDismiss}
                  onUndismiss={handleUndismiss}
                  onAddSection={handleAddSection}
                  onToggleTypeDropdown={() => setTypeDropdownOpen(!typeDropdownOpen)}
                  onStartDismiss={(id) => { setDismissingId(id); setDismissReason(''); setDismissError(''); }}
                  onCancelDismiss={() => { setDismissingId(null); setDismissReason(''); setDismissError(''); }}
                  onDismissReasonChange={setDismissReason}
                  onToggleAddSection={() => { setShowAddSection(!showAddSection); setAddingSectionError(''); }}
                  onOpenAcceptUnexpected={handleOpenAcceptUnexpected}
                />
              )}
            </div>
          );
        })}
      </div>

      {filteredSubmissions.length === 0 && filterNeedsAttention && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <CircleCheck className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <p className="text-sm text-slate-500">All submissions are classified with no missing sections.</p>
        </div>
      )}

      {saving && (
        <div className="fixed inset-0 bg-black/10 flex items-center justify-center z-50">
          <LoadingSpinner message="Saving..." />
        </div>
      )}

      {/* A2F-565: bulk-accept observed-but-unexpected sections dialog. */}
      {acceptUnexpectedOpen && expandedSubId && (() => {
        const cls = classifications.get(expandedSubId);
        const observed = cls?.sections.filter((s) => s.basis === 'observed') ?? [];
        const sections = observed.map((s) => ({
          sectionNumber: s.sectionNumber,
          module: s.module,
        }));
        const sectionNumbers = observed.map((s) => s.sectionNumber);
        const sub = submissions.find((s) => s.id === expandedSubId);
        const label = sub ? `SUB ${sub.displaySequence}` : 'This submission';
        return (
          <AcceptUnexpectedDialog
            submissionLabel={label}
            sections={sections}
            submitting={acceptingUnexpected}
            error={acceptUnexpectedError}
            driftedSections={driftedSections}
            onConfirm={() => handleConfirmAcceptUnexpected(sectionNumbers)}
            onClose={handleCloseAcceptUnexpected}
          />
        );
      })()}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Expanded Panel — all the detail for one submission
// ---------------------------------------------------------------------------

function ExpandedPanel({
  sub, classification, typesByCategory, allTypes,
  inferring, analyzing, analyzeError, saving, refreshing,
  typeDropdownOpen, dismissingId, dismissReason, dismissError,
  showAddSection, addingSectionError,
  canRunScans, canDismissFindings, devMode,
  fileInputRef, triagePanelRef,
  onSetType, onConfirm, onReset, onRefreshPresence,
  onAnalyzeLetter, onClearAnalysis,
  onDismiss, onUndismiss, onAddSection,
  onToggleTypeDropdown, onStartDismiss, onCancelDismiss, onDismissReasonChange,
  onToggleAddSection,
  onOpenAcceptUnexpected,
}: {
  sub: Submission;
  classification: SubmissionClassification | null;
  typesByCategory: Record<string, SubmissionTypeOption[]>;
  allTypes: SubmissionTypeOption[];
  inferring: boolean;
  analyzing: boolean;
  analyzeError: string;
  saving: boolean;
  refreshing: boolean;
  typeDropdownOpen: boolean;
  dismissingId: string | null;
  dismissReason: string;
  dismissError: string;
  showAddSection: boolean;
  addingSectionError: string;
  canRunScans: boolean;
  canDismissFindings: boolean;
  devMode: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  triagePanelRef: React.RefObject<HTMLDivElement | null>;
  onSetType: (code: string) => void;
  onConfirm: () => void;
  onReset: () => void;
  onRefreshPresence: () => void;
  onAnalyzeLetter: (file: File) => void;
  onClearAnalysis: () => void;
  onDismiss: (id: string) => void;
  onUndismiss: (id: string) => void;
  onAddSection: (sectionNumber: string) => void;
  onToggleTypeDropdown: () => void;
  onStartDismiss: (id: string) => void;
  onCancelDismiss: () => void;
  onDismissReasonChange: (v: string) => void;
  onToggleAddSection: () => void;
  onOpenAcceptUnexpected: () => void;
}) {
  const hasResolveAnalysis = classification?.hasResolveAnalysis ?? false;
  const selectTypeButtonRef = useRef<HTMLButtonElement>(null);
  const changeTypeButtonRef = useRef<HTMLButtonElement>(null);

  // Group sections by module. Each module column renders recommended rows
  // (original order) first, then observed rows (alpha by section number) —
  // observed rows get a distinct amber treatment below to surface
  // misclassification rather than hide it.
  const sectionsByModule: Record<string, SectionSuggestion[]> = {};
  if (classification?.sections) {
    for (const s of classification.sections) {
      (sectionsByModule[s.module] ??= []).push(s);
    }
    for (const mod of Object.keys(sectionsByModule)) {
      sectionsByModule[mod].sort((a, b) => {
        if (a.isRecommended !== b.isRecommended) return a.isRecommended ? -1 : 1;
        if (!a.isRecommended) return a.sectionNumber.localeCompare(b.sectionNumber);
        return 0;
      });
    }
  }
  const moduleKeys = Object.keys(sectionsByModule).sort();

  // A2F-565: count of observed-but-unexpected rows — drives the
  // "Accept all N unexpected" toolbar button visibility.
  const unexpectedCount = classification?.sections.filter(
    (s) => s.basis === 'observed',
  ).length ?? 0;

  return (
    <div className="border-t border-slate-100 px-5 pb-5 space-y-5">
      {/* Classification Triage Panel */}
      <div ref={triagePanelRef} className="rounded-xl border-2 border-brand-orange/30 p-5 mt-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
            <Zap className="w-5 h-5 text-brand-orange" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-brand-charcoal">Classification Triage</h2>

            {!classification?.typeCode ? (
              <div className="mt-3">
                {inferring ? (
                  <div className="flex items-center gap-2 mb-3">
                    <LoaderCircle className="w-4 h-4 text-brand-orange animate-spin" />
                    <span className="text-sm text-slate-500">Analyzing submission sections...</span>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-slate-500 mb-3">
                      No submission type assigned. Select a type to see recommended CTD sections.
                    </p>
                    <div className="inline-block">
                      <button
                        ref={selectTypeButtonRef}
                        onClick={onToggleTypeDropdown}
                        disabled={!canRunScans}
                        title={!canRunScans ? 'Requires Contributor role' : undefined}
                        className={`flex items-center gap-2 px-4 py-2 bg-brand-orange text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors${!canRunScans ? ' opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Select Type
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      {typeDropdownOpen && (
                        <TypeDropdown
                          anchorRef={selectTypeButtonRef}
                          typesByCategory={typesByCategory}
                          onSelect={onSetType}
                          onClose={onToggleTypeDropdown}
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="mt-2 space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-600">
                    {classification.typeState === 'inferred'
                      ? <>Auto-detected as{' '}<span className="font-semibold text-brand-orange">{classification.typeLabel}</span></>
                      : <>Classified as{' '}<span className="font-semibold text-brand-orange">{classification.typeLabel}</span></>
                    }
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                    classification.typeState === 'validated'
                      ? 'bg-green-100 text-green-700'
                      : classification.typeState === 'declared'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-amber-100 text-amber-700'
                  }`}>
                    {classification.typeState}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {classification.typeState === 'inferred' && (
                    <button
                      onClick={onConfirm}
                      disabled={saving || !canRunScans}
                      title={!canRunScans ? 'Requires Contributor role' : undefined}
                      className={`flex items-center gap-1.5 px-3 py-1.5 bg-brand-orange text-white text-xs font-medium rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50${!canRunScans ? ' cursor-not-allowed' : ''}`}
                    >
                      <Check className="w-3.5 h-3.5" />
                      Confirm
                    </button>
                  )}

                  <div>
                    <button
                      ref={changeTypeButtonRef}
                      onClick={onToggleTypeDropdown}
                      disabled={!canRunScans}
                      title={!canRunScans ? 'Requires Contributor role' : undefined}
                      className={`flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-50 transition-colors${!canRunScans ? ' opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Change Type
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    {typeDropdownOpen && (
                      <TypeDropdown
                        anchorRef={changeTypeButtonRef}
                        typesByCategory={typesByCategory}
                        onSelect={onSetType}
                        onClose={onToggleTypeDropdown}
                        currentCode={classification.typeCode ?? undefined}
                        onReset={onReset}
                      />
                    )}
                  </div>

                  <button
                    onClick={onRefreshPresence}
                    disabled={refreshing}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                    Recheck Sections
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FDA Letter upload for resolve-powered types — gated behind dev mode */}
      {classification?.resolvePowered && classification?.typeCode && (
        <div className={`rounded-xl border border-slate-200 p-5${!devMode ? ' opacity-50' : ''}`}>
          <div className="flex items-start gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${devMode ? 'bg-blue-50' : 'bg-slate-100'}`}>
              {devMode ? (
                <FileSearch className="w-5 h-5 text-blue-600" />
              ) : (
                <Lock className="w-5 h-5 text-slate-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={`text-sm font-semibold ${devMode ? 'text-brand-charcoal' : 'text-slate-400'}`}>FDA Letter Analysis</h3>
              {!devMode ? (
                <p className="text-xs text-slate-400 mt-1">This feature is not available yet.</p>
              ) : hasResolveAnalysis ? (
                <div className="mt-2">
                  <p className="text-xs text-green-600 font-medium">
                    FDA letter analyzed — {classification.sections.filter(s => s.basis === 'resolve').length} additional sections identified.
                  </p>
                  <div className="mt-2 flex items-center gap-3">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={analyzing || !canRunScans}
                      title={!canRunScans ? 'Requires Contributor role' : undefined}
                      className={`text-xs text-slate-500 hover:text-slate-700 underline${!canRunScans ? ' opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Re-analyze with a different file
                    </button>
                    <button
                      onClick={onClearAnalysis}
                      disabled={saving || !canRunScans}
                      title={!canRunScans ? 'Requires Contributor role' : undefined}
                      className={`text-xs text-red-500 hover:text-red-700 underline${!canRunScans ? ' opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Clear Analysis
                    </button>
                  </div>
                </div>
              ) : analyzing ? (
                <div className="mt-3 flex items-center gap-2">
                  <LoaderCircle className="w-4 h-4 text-brand-orange animate-spin" />
                  <span className="text-sm text-slate-500">Analyzing FDA letter...</span>
                </div>
              ) : (
                <div className="mt-2">
                  <p className="text-xs text-slate-500 mb-3">
                    Upload the FDA letter to identify specific impacted sections.
                  </p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!canRunScans}
                    title={!canRunScans ? 'Requires Contributor role' : undefined}
                    className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-600 hover:border-brand-orange hover:text-brand-orange transition-colors${!canRunScans ? ' opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Upload className="w-4 h-4" />
                    Choose FDA Letter (PDF or Word)
                  </button>
                </div>
              )}
              {analyzeError && <p className="mt-2 text-xs text-red-500">{analyzeError}</p>}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onAnalyzeLetter(f); }}
                className="hidden"
              />
            </div>
          </div>
        </div>
      )}

      {/* Section summary stats */}
      {classification?.typeCode && classification.sections.length > 0 && (
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-sm">
            <span className="font-semibold text-brand-charcoal">{classification.totalSuggested}</span>
            <span className="text-slate-500">sections recommended</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <CircleCheck className="w-4 h-4 text-green-500" />
            <span className="font-medium text-green-700">{classification.presentCount} present</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <Circle className="w-4 h-4 text-slate-300" />
            <span className="font-medium text-slate-500">{classification.missingCount} missing</span>
          </div>
        </div>
      )}

      {/* Add Section + Accept Unexpected toolbar */}
      {classification?.typeCode && canRunScans && (
        <div>
          {showAddSection ? (
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-brand-charcoal">Add a section</h3>
                <button onClick={onToggleAddSection} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <SectionPicker onAssign={onAddSection} onDismiss={onToggleAddSection} />
              {addingSectionError && <p className="text-xs text-red-500 mt-2">{addingSectionError}</p>}
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <button
                onClick={onToggleAddSection}
                className="flex items-center gap-1.5 text-sm text-brand-orange hover:text-brand-orange/80 font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Section
              </button>
              {/* A2F-565: bulk-accept observed-but-unexpected sections. Hidden
                  when zero, matching the "only show actionable state" pattern. */}
              {unexpectedCount > 0 && (
                <button
                  onClick={onOpenAcceptUnexpected}
                  className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-charcoal font-medium transition-colors"
                >
                  <CircleCheck className="w-4 h-4" />
                  Accept all {unexpectedCount} unexpected
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recommended CTD Sections — grouped by module */}
      {classification?.typeCode && moduleKeys.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {moduleKeys.map((mod) => {
            const sections = sectionsByModule[mod];
            return (
              <div key={mod} className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-orange-100 flex items-center gap-2 bg-orange-50">
                  <span className="text-xs font-semibold text-brand-orange">Module {mod}</span>
                  <span className="text-xs text-brand-orange/70">{moduleLabel(mod)}</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {sections.map((s) => (
                    <SuggestionRow
                      key={s.id ?? `observed-${s.sectionNumber}`}
                      suggestion={s}
                      dismissingId={dismissingId}
                      dismissReason={dismissReason}
                      dismissError={dismissError}
                      canDismiss={canDismissFindings}
                      onStartDismiss={() => s.id && onStartDismiss(s.id)}
                      onCancelDismiss={onCancelDismiss}
                      onDismissReasonChange={onDismissReasonChange}
                      onDismiss={() => s.id && onDismiss(s.id)}
                      onUndismiss={() => s.id && onUndismiss(s.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state when type set but no sections */}
      {classification?.typeCode && classification.sections.length === 0 && (
        <div className="rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-400">
            {classification.resolvePowered
              ? 'Upload the FDA letter above to identify impacted sections.'
              : 'No section recommendations for this submission type.'}
          </p>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TypeDropdown({
  anchorRef,
  typesByCategory,
  onSelect,
  onClose,
  currentCode,
  onReset,
}: {
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  typesByCategory: Record<string, SubmissionTypeOption[]>;
  onSelect: (code: string) => void;
  onClose: () => void;
  currentCode?: string;
  onReset?: () => void;
}) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const updatePos = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) setPos({ top: rect.bottom + 4, left: rect.left });
    };
    // Close on outside page scroll so the fixed-position dropdown doesn't
    // visually detach from its anchor. Ignore scrolls that originate
    // inside the dropdown's own overflow container — otherwise scrolling
    // the type list immediately closes it (A2F-480).
    const handleScroll = (e: Event) => {
      const target = e.target;
      if (
        dropdownRef.current &&
        target instanceof Node &&
        dropdownRef.current.contains(target)
      ) {
        return;
      }
      onClose();
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [anchorRef, onClose]);

  if (!pos) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div
        ref={dropdownRef}
        style={{ top: pos.top, left: pos.left }}
        className="fixed w-80 bg-white border border-slate-200 rounded-lg shadow-lg z-40 max-h-80 overflow-y-auto"
      >
        {onReset && (
          <button
            onClick={() => { onReset(); onClose(); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 transition-colors text-slate-500 border-b border-slate-200"
          >
            <p className="font-medium">Not Classified</p>
            <p className="text-xs text-slate-400">Clear type and remove section suggestions</p>
          </button>
        )}
        {Object.entries(typesByCategory)
          .sort(([a], [b]) => {
            const ai = CATEGORY_ORDER.indexOf(a);
            const bi = CATEGORY_ORDER.indexOf(b);
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
          })
          .map(([cat, types]) => (
          <div key={cat}>
            <div className="px-3 py-1.5 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider sticky top-0">
              {CATEGORY_LABELS[cat] ?? cat}
            </div>
            {types.map((t) => (
              <button
                key={t.code}
                onClick={() => onSelect(t.code)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 transition-colors flex items-center justify-between ${
                  t.code === currentCode ? 'bg-orange-50 text-brand-orange' : 'text-brand-charcoal'
                }`}
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{t.label}</p>
                  <p className="text-xs text-slate-400 truncate">{t.description}</p>
                </div>
                <span className="text-xs text-slate-400 shrink-0 ml-2">{t.primarySectionsCount} sections</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </>,
    document.body
  );
}

function SuggestionRow({
  suggestion,
  dismissingId,
  dismissReason,
  dismissError,
  canDismiss,
  onStartDismiss,
  onCancelDismiss,
  onDismissReasonChange,
  onDismiss,
  onUndismiss,
}: {
  suggestion: SectionSuggestion;
  dismissingId: string | null;
  dismissReason: string;
  dismissError: string;
  canDismiss: boolean;
  onStartDismiss: () => void;
  onCancelDismiss: () => void;
  onDismissReasonChange: (v: string) => void;
  onDismiss: () => void;
  onUndismiss: () => void;
}) {
  const s = suggestion;
  const isDismissing = s.id !== null && dismissingId === s.id;

  if (!s.isRecommended) {
    // Observed row — doc exists in a section not recommended for this
    // classification. Surface as a warning to flag potential misclassification.
    // Display-only in v1: no dismiss, no id, synthesized per-request.
    return (
      <div className="px-4 py-3 flex items-center gap-3 bg-amber-50/30">
        <div className="w-5 h-5 flex items-center justify-center shrink-0">
          <TriangleAlert className="w-4 h-4 text-amber-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-brand-charcoal">
            {s.sectionNumber}
            {s.sectionTitle ? ` — ${s.sectionTitle}` : ''}
            <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
              Unexpected section
            </span>
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            Document present in a section not expected for this submission type.
          </p>
        </div>
      </div>
    );
  }

  if (s.dismissed) {
    return (
      <div className="px-4 py-3 flex items-center gap-3 bg-slate-50/50 opacity-60">
        <div className="w-5 h-5 flex items-center justify-center shrink-0">
          <X className="w-4 h-4 text-slate-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-400 line-through">
            {s.sectionNumber} — {s.sectionTitle}
          </p>
          {s.dismissReason && (
            <p className="text-xs text-slate-400 mt-0.5 italic">{s.dismissReason}</p>
          )}
        </div>
        <button
          onClick={onUndismiss}
          disabled={!canDismiss}
          title={!canDismiss ? 'Requires Contributor role' : undefined}
          className={`text-xs text-brand-orange hover:underline shrink-0${!canDismiss ? ' opacity-50 cursor-not-allowed' : ''}`}
        >
          Restore
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="w-5 h-5 flex items-center justify-center shrink-0">
          <StatusBadge status={s.isPresent ? 'present' : 'missing_section'} feature="submission_classification" iconSize="w-5 h-5" pill={false} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-brand-charcoal">
            {s.sectionNumber} — {s.sectionTitle}
            {s.basis === 'manual' && (
              <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-medium">Manual</span>
            )}
          </p>
          {s.rationale && (
            <p className="text-xs text-slate-400 mt-0.5">{s.rationale}</p>
          )}
        </div>
        <button
          onClick={onStartDismiss}
          disabled={!canDismiss}
          title={!canDismiss ? 'Requires Contributor role' : undefined}
          className={`text-xs text-slate-400 hover:text-slate-600 shrink-0${!canDismiss ? ' opacity-50 cursor-not-allowed' : ''}`}
        >
          Dismiss
        </button>
      </div>

      {isDismissing && (
        <div className="mt-2 ml-8 flex items-center gap-2">
          <input
            type="text"
            value={dismissReason}
            onChange={(e) => onDismissReasonChange(e.target.value)}
            placeholder="Reason for dismissal (min 10 chars)..."
            className="flex-1 text-xs px-2.5 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-orange"
            onKeyDown={(e) => { if (e.key === 'Enter') onDismiss(); }}
            autoFocus
          />
          <button
            onClick={onDismiss}
            className="text-xs px-2.5 py-1.5 bg-brand-orange text-white rounded-md hover:bg-orange-600 transition-colors"
          >
            Confirm
          </button>
          <button
            onClick={onCancelDismiss}
            className="text-xs px-2.5 py-1.5 text-slate-500 hover:text-slate-700"
          >
            Cancel
          </button>
          {dismissError && <span className="text-xs text-red-500">{dismissError}</span>}
        </div>
      )}
    </div>
  );
}
